import { Link } from '@tanstack/react-router'
import { Sparkles } from 'lucide-react'
import { Meter } from '@/components/ui/progress'
import { useMe } from '@/features/auth/api'
import { formatUsage, usageRatio, usageTone } from '@/lib/format'
import { cn } from '@/lib/utils'

function daysUntil(iso: string | null): number | null {
  if (!iso) return null
  const diff = new Date(iso).getTime() - Date.now()
  return diff > 0 ? Math.ceil(diff / 86_400_000) : 0
}

/** Compact plan + display usage card at the bottom of the sidebar. */
export function PlanFooter({ collapsed }: { collapsed: boolean }) {
  const { data: me } = useMe()
  const sub = me?.subscription
  if (!sub || collapsed) return null
  const displays = sub.entitlements.limits.displays
  const ratio = displays ? usageRatio(displays.used, displays.limit) : null
  const trialDays = sub.status === 'trialing' ? daysUntil(sub.trial_ends_at) : null
  return (
    <div className="mx-3 mb-3 rounded-xl border border-sidebar-border bg-card/60 p-3 shadow-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[13px] font-semibold">{sub.plan.name}</span>
        <span className={cn('rounded-full px-1.5 py-px text-[10.5px] font-medium', sub.status === 'trialing' ? 'bg-primary/12 text-primary' : 'bg-success/12 text-success')}>
          {trialDays !== null ? `Trial · ${trialDays}d left` : sub.status === 'active' ? 'Active' : sub.status.replace('_', ' ')}
        </span>
      </div>
      {displays && (
        <div className="mt-2.5 space-y-1.5">
          <div className="flex justify-between text-[11.5px] text-muted-foreground">
            <span>Displays</span>
            <span className="tabular">{formatUsage('displays', displays.used, displays.limit)}</span>
          </div>
          <Meter value={ratio} tone={usageTone(ratio) === 'ok' ? 'brand' : usageTone(ratio) === 'warning' ? 'warning' : 'critical'} size="sm" label="Display usage" />
        </div>
      )}
      <Link
        to="/settings/subscription"
        className="mt-2.5 flex items-center gap-1.5 text-[12px] font-medium text-primary outline-none hover:underline focus-visible:underline"
      >
        <Sparkles className="size-3.5" /> Manage plan
      </Link>
    </div>
  )
}
